/**
 * G-91 — quantity, unit price and invoice total, in one place.
 *
 * The receive endpoints take an INVOICE TOTAL. People hold two different
 * numbers: a delivery note says "37.50 for the box", a price sticker says
 * "2.50 each". Both screens that receive stock therefore have to convert, and
 * before this module both did it inline — the kiosk multiplied with its own
 * `toFixed(2)` (G-86) and the CRM modal did not offer the unit price at all.
 *
 * Two copies of one arithmetic rule is how the client-side VAT calculation
 * G-30 deleted came to disagree with the server's. This is the only copy.
 */

/** The total an invoice would show for `quantity` units at `unitPriceEur` each. */
export function invoiceTotalFromUnitPrice(unitPriceEur: number, quantity: number): number {
  return Number((unitPriceEur * quantity).toFixed(2));
}

/**
 * What one unit cost, given the total on the note.
 *
 * Null when the quantity cannot divide — an unknown is better than a number
 * that says a stem cost Infinity euros.
 */
export function unitPriceFromInvoiceTotal(
  invoiceTotalEur: number,
  quantity: number,
): number | null {
  if (!Number.isFinite(invoiceTotalEur) || !Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }
  return invoiceTotalEur / quantity;
}

/**
 * D-54: the quantity a unit PRICE is quoted for. A flower batch is billed for
 * every stem on the note, usable or not, so its price multiplies the INVOICED
 * quantity; the server then spreads that total over the usable stems. Using
 * the usable quantity here understated the spend by the receiving waste.
 */
export function priceQuantity(receipt: {
  byBatch: boolean;
  quantity: number;
  invoicedQty: number;
}): number {
  return receipt.byBatch ? receipt.invoicedQty : receipt.quantity;
}

/**
 * What one USABLE stem really costs once the unusable ones are paid for —
 * the figure the server stores. Null when there is no waste to explain.
 */
export function usableStemCost(
  invoiceTotalEur: number,
  invoicedQty: number,
  usableQty: number,
): number | null {
  if (!(usableQty > 0) || !(invoicedQty > usableQty)) return null;
  return unitPriceFromInvoiceTotal(invoiceTotalEur, usableQty);
}

/**
 * A per-unit price as shown: cents when it is a whole number of cents,
 * otherwise four decimals rather than a rounded figure that disagrees with
 * the stored cost. "Whole cents" is judged with a tolerance: 2.13 × 100 is
 * 212.99999999999997 in binary floating point.
 */
export function formatUnitPrice(unitPriceEur: number): string {
  const cents = unitPriceEur * 100;
  return Math.abs(cents - Math.round(cents)) < 1e-6
    ? unitPriceEur.toFixed(2)
    : unitPriceEur.toFixed(4);
}

/**
 * Parse a money/quantity box the way both screens need it: blank is "not
 * given", anything unparseable or negative is a refusal, never a silent zero.
 * A zero the user actually typed is kept — giving stock away is a decision
 * someone may really make, and G-45 is explicit that a derived zero is not.
 */
export function parsePositiveAmount(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return "invalid";
  return value;
}
